const API_BASE = "/api";

let authToken: string | null = null;

export function setCustomerAuthToken(token: string | null) {
  authToken = token;
}

export function getCustomerAuthToken(): string | null {
  return authToken;
}

export function clearCustomerAuthToken() {
  authToken = null;
}

async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };

  if (authToken) {
    (headers as Record<string, string>)["Authorization"] = `Bearer ${authToken}`;
  }

  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Errore sconosciuto" }));
    throw new Error(error.error || "Errore di rete");
  }

  return response.json();
}

export interface CustomerAccount {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  fiscalCode?: string;
  vatNumber?: string;
  companyName?: string;
  isVerified: boolean;
  privacyAccepted: boolean;
  marketingConsent: boolean;
}

export interface CustomerAddress {
  id: string;
  customerAccountId: string;
  type: "shipping" | "billing";
  isDefault: boolean;
  firstName: string;
  lastName: string;
  companyName?: string;
  phone?: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CartItem {
  id: string;
  productType: "watch" | "jewelry" | "investment";
  productId: string;
  quantity: number;
  priceAtAdd: string;
  createdAt: string;
  updatedAt: string;
}

export interface Cart {
  items: CartItem[];
  subtotal: string;
  itemCount: number;
}

export interface Order {
  id: string;
  orderNumber: string;
  status: string;
  paymentMethod: string;
  subtotal: string;
  shippingCost: string;
  vatAmount: string;
  total: string;
  shippingAddress: any;
  billingAddress: any;
  trackingNumber?: string;
  customerNotes?: string;
  createdAt: string;
  updatedAt: string;
  items?: any[];
  statusHistory?: any[];
}

export async function register(data: {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  privacyAccepted: boolean;
  marketingConsent?: boolean;
}) {
  return fetchWithAuth("/auth/customer/register", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function verifyEmail(token: string) {
  return fetchWithAuth("/auth/customer/verify-email", {
    method: "POST",
    body: JSON.stringify({ token }),
  });
}

export async function login(email: string, password: string, rememberMe: boolean = false) {
  const response = await fetchWithAuth("/auth/customer/login", {
    method: "POST",
    body: JSON.stringify({ email, password, rememberMe }),
  });
  return response as {
    message: string;
    token: string;
    refreshToken: string;
    customer: CustomerAccount;
  };
}

export async function refreshToken(refreshToken: string) {
  return fetchWithAuth("/auth/customer/refresh", {
    method: "POST",
    body: JSON.stringify({ refreshToken }),
  }) as Promise<{ token: string; refreshToken: string }>;
}

export async function forgotPassword(email: string) {
  return fetchWithAuth("/auth/customer/forgot-password", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export async function resetPassword(token: string, password: string) {
  return fetchWithAuth("/auth/customer/reset-password", {
    method: "POST",
    body: JSON.stringify({ token, password }),
  });
}

export async function getCurrentCustomer(): Promise<CustomerAccount> {
  return fetchWithAuth("/auth/customer/me");
}

export async function updateProfile(data: Partial<CustomerAccount>) {
  return fetchWithAuth("/auth/customer/profile", {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function changePassword(currentPassword: string, newPassword: string) {
  return fetchWithAuth("/auth/customer/change-password", {
    method: "POST",
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

export async function getAddresses(): Promise<CustomerAddress[]> {
  return fetchWithAuth("/customer/addresses");
}

export async function createAddress(data: Omit<CustomerAddress, "id" | "customerAccountId" | "createdAt" | "updatedAt">) {
  return fetchWithAuth("/customer/addresses", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateAddress(id: string, data: Partial<CustomerAddress>) {
  return fetchWithAuth(`/customer/addresses/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteAddress(id: string) {
  return fetchWithAuth(`/customer/addresses/${id}`, {
    method: "DELETE",
  });
}

export async function getCart(): Promise<Cart> {
  return fetchWithAuth("/cart");
}

export async function addToCart(productType: string, productId: string, quantity: number = 1) {
  return fetchWithAuth("/cart/add", {
    method: "POST",
    body: JSON.stringify({ productType, productId, quantity }),
  });
}

export async function updateCartItem(productType: string, productId: string, quantity: number) {
  return fetchWithAuth("/cart/update", {
    method: "PUT",
    body: JSON.stringify({ productType, productId, quantity }),
  });
}

export async function removeFromCart(productType: string, productId: string) {
  return fetchWithAuth("/cart/remove", {
    method: "DELETE",
    body: JSON.stringify({ productType, productId }),
  });
}

export async function clearCart() {
  return fetchWithAuth("/cart/clear", {
    method: "DELETE",
  });
}

export async function checkout(data: {
  shippingAddressId?: string;
  billingAddressId?: string;
  sameAsShipping?: boolean;
  customerNotes?: string;
  newShippingAddress?: Partial<CustomerAddress>;
  newBillingAddress?: Partial<CustomerAddress>;
}) {
  return fetchWithAuth("/orders/checkout", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getOrders(): Promise<Order[]> {
  return fetchWithAuth("/orders");
}

export async function getOrderById(id: string): Promise<Order> {
  return fetchWithAuth(`/orders/${id}`);
}
